import { logger } from '@/lib/logger';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { useEffect, useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, RefreshCcw } from 'lucide-react';
import { firebaseAuth } from '@/lib/firebase';
import { AI_BASE_URL } from '@/lib/ai-base-url';
import { useToast } from '@/hooks/use-toast';

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * ProtectedRoute — guards authenticated-only pages.
 *
 * Firebase handles session persistence natively (no Safari workarounds needed).
 * Checks AuthProvider state, redirects if unauthenticated, and gates
 * unverified emails with a verification prompt (admins bypass).
 */
export function ProtectedRoute({
  children,
  redirectTo = "/auth"
}: ProtectedRouteProps) {
  const { user, isAuthResolved } = useAuth();
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [authFailureTimeout, setAuthFailureTimeout] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => { isMountedRef.current = false; };
  }, []);

  // Safety timeout for loading state
  useEffect(() => {
    if (isAuthResolved || loadingTimeout) return;

    const timeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        logger.debug('ProtectedRoute: Loading safety timeout triggered');
        setLoadingTimeout(true);
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [isAuthResolved, loadingTimeout]);

  // Maximum wait before forcing redirect
  useEffect(() => {
    if (isAuthResolved || authFailureTimeout) return;

    const failureTimeoutId = setTimeout(() => {
      if (isMountedRef.current && !isAuthResolved) {
        logger.warn('ProtectedRoute: Auth resolution timed out after 10 seconds');
        setAuthFailureTimeout(true);
      }
    }, 10000);

    return () => clearTimeout(failureTimeoutId);
  }, [isAuthResolved, authFailureTimeout]);

  // Auth timeout — force redirect to login
  if (authFailureTimeout && !isAuthResolved) {
    return <Navigate to={redirectTo} replace />;
  }

  // Still loading auth state — show skeleton that preserves page structure
  if (!isAuthResolved) {
    return (
      <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900">
        {/* Skeleton navbar */}
        <div className="h-16 flex-shrink-0 glass dark:glass-dark border-b border-gray-200/50 dark:border-gray-700/50 flex items-center px-4 gap-3">
          <div className="h-8 w-8 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
          <div className="h-4 w-32 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
        </div>
        {/* Skeleton content area */}
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-500 dark:text-gray-400">
              {loadingTimeout ? 'Taking longer than expected...' : 'Checking authorization...'}
            </p>
            {loadingTimeout && (
              <p className="mt-2 text-sm text-gray-400 dark:text-gray-500 max-w-md text-center">
                This is taking longer than usual. If this persists, try refreshing the page.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Authenticated — check email verification (admins bypass)
  if (user) {
    const isAdmin = user.user_metadata?.is_admin === true;
    if (!user.emailVerified && !isAdmin) {
      return <EmailVerificationGate email={user.email} />;
    }
    return <><OnVerifiedTrigger userId={user.id} />{children}</>;
  }

  // Not authenticated — redirect
  return <Navigate to={redirectTo} />;
}

/**
 * Inline verification gate — shown when user is signed in but email is unverified.
 * Calls the server-side resend endpoint and refreshes Firebase token on "I've verified".
 */
/**
 * Invisible component that fires /pay/on-verified once per session
 * to ensure verified users get a Stripe Customer created via Stripe SDK.
 */
const onVerifiedFired = new Set<string>();
function OnVerifiedTrigger({ userId }: { userId: string }) {
  useEffect(() => {
    if (onVerifiedFired.has(userId)) return;
    onVerifiedFired.add(userId);

    const currentUser = firebaseAuth.currentUser;
    if (!currentUser) return;

    const paymentBaseUrl = import.meta.env.VITE_PAYMENT_API_URL || AI_BASE_URL;
    currentUser.getIdToken().then((t) => {
      fetch(`${paymentBaseUrl}/pay/on-verified`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
      }).catch(() => {/* fire-and-forget */});
    }).catch(() => {/* token refresh failed — will retry next session */});
  }, [userId]);

  return null;
}

function EmailVerificationGate({ email }: { email: string | null }) {
  const [isResending, setIsResending] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { toast } = useToast();
  const { signOut } = useAuth();

  const handleResend = useCallback(async () => {
    if (!email) return;
    setIsResending(true);
    try {
      const paymentBaseUrl = import.meta.env.VITE_PAYMENT_API_URL || AI_BASE_URL;
      const response = await fetch(`${paymentBaseUrl}/pay/signup/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.status === 429) {
        toast({ title: 'Please wait', description: data.error, variant: 'default' });
      } else if (!response.ok) {
        toast({ title: 'Error', description: 'Failed to resend email. Please try again.', variant: 'destructive' });
      } else {
        toast({ title: 'Email Sent', description: `Verification email resent to ${email}.`, variant: 'default' });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to resend email. Please try again.', variant: 'destructive' });
    } finally {
      setIsResending(false);
    }
  }, [email, toast]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const currentUser = firebaseAuth.currentUser;
      if (currentUser) {
        await currentUser.reload();
        // Force token refresh — triggers onIdTokenChanged in AuthProvider
        // If email is now verified, emailVerified will flip to true and gate clears
        await currentUser.getIdToken(true);

        if (!currentUser.emailVerified) {
          toast({
            title: 'Not yet verified',
            description: 'Please click the verification link in your email first.',
            variant: 'default',
          });
        }
      }
    } catch (err) {
      logger.error('Failed to refresh verification status:', err);
      toast({ title: 'Error', description: 'Failed to check status. Please try again.', variant: 'destructive' });
    } finally {
      setIsRefreshing(false);
    }
  }, [toast]);

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50 dark:from-gray-900 dark:to-gray-800 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-4 p-3 bg-orange-100 dark:bg-orange-900/30 rounded-full w-fit">
            <Mail className="w-8 h-8 text-orange-600 dark:text-orange-400" />
          </div>
          <CardTitle className="text-2xl font-bold dark:text-white">Verify Your Email</CardTitle>
        </CardHeader>

        <CardContent className="text-center space-y-4">
          <p className="text-gray-600 dark:text-gray-300">
            Please verify your email address to access the app.
          </p>
          {email && (
            <p className="font-medium text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-800 px-3 py-2 rounded">
              {email}
            </p>
          )}

          <div className="space-y-3 pt-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Click the link in the verification email we sent you, then tap the button below.
            </p>

            <Button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="w-full bg-[#F37022] hover:bg-[#E36012] text-white"
            >
              {isRefreshing ? (
                <>
                  <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                  Checking...
                </>
              ) : (
                "I've Verified My Email"
              )}
            </Button>

            <Button
              variant="outline"
              onClick={handleResend}
              disabled={isResending}
              className="w-full"
            >
              {isResending ? (
                <>
                  <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                "Resend Verification Email"
              )}
            </Button>

            <div className="pt-4 border-t dark:border-gray-700">
              <Button
                variant="link"
                onClick={() => signOut()}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                Sign out and use a different account
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
